import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { imageUrl } from "@/lib/api";
import { Loader2, Eye, Users, Repeat, UserPlus, MousePointerClick, Percent, Heart, Star, MessageCircle, Coins, ShoppingBag, Globe, TrendingUp, Video, DollarSign } from "lucide-react";
import BrandName from "@/components/BrandName";

const WINDOWS = [
  { key: "last24h", label: "24h" },
  { key: "last7d", label: "7 days" },
  { key: "last30d", label: "30 days" },
  { key: "all", label: "All" },
];

const Stat = ({ icon: Icon, label, value, tint = "#3f6238", sub }) => (
  <div className="sms-tweed rounded-xl border-2 border-[#5d8a52] bg-[#0e0e13] px-3 py-2.5 flex items-center gap-2.5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <Icon size={18} style={{ color: tint }} className="shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-white/50 leading-tight">{label}</div>
      <div className="text-base font-extrabold text-white leading-tight">{value}{sub && <span className="text-[11px] font-semibold text-white/45 ml-1">{sub}</span>}</div>
    </div>
  </div>
);

const Bars = ({ data, labelLeft, labelRight, valueKey = "views", height = 90 }) => {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  const dense = data.length > 14;  // 30-day / All view → slimmer bars, hide per-bar numbers to stay clean
  return (
    <div>
      <div className={`flex items-end ${dense ? "gap-px" : "gap-1"}`}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div className="w-full flex items-end" style={{ height }}>
              <div
                className="w-full rounded-t-md bg-[#1e3aaa] hover:bg-[#2a4ac0] transition-colors"
                style={{ height: `${Math.max(3, ((d[valueKey] || 0) / max) * 100)}%` }}
                title={`${d.date || d.label || i}: ${d[valueKey] || 0}`}
              />
            </div>
            {!dense && <span className="text-[9px] font-bold text-white/60 tabular-nums leading-none">{d[valueKey] || 0}</span>}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-white/40 mt-1.5 font-semibold">
        <span>{labelLeft}</span><span>{labelRight}</span>
      </div>
    </div>
  );
};

export default function Analytics({ content = "video" }) {
  const navigate = useNavigate();
  const isPod = content === "podcast";
  const [window, setWindow] = useState("last7d");
  const [traffic, setTraffic] = useState(null);
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/studio/analytics", { params: { content } }).then(({ data }) => setStudio(data)).catch(() => {});
  }, [content]);

  useEffect(() => {
    setLoading(true);
    api.post("/me/studio/traffic", { window, content }).then(({ data }) => setTraffic(data)).catch(() => {}).finally(() => setLoading(false));
  }, [window, content]);

  const t = studio?.totals || {};
  const k = traffic?.kpis || {};
  const hourly = traffic?.hourly || [];
  const series = traffic?.series || traffic?.daily || [];
  const rangeLabel = traffic?.range_label || "last 7 days";
  const money = useMemo(() => (((t.tips || 0) / 100) + (t.sales_earned || 0)), [t]);

  return (
    <div className="max-w-5xl mx-auto px-4 pt-6 pb-16">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap sms-outline-dark" data-testid="analytics-title">
            <BrandName dark />
            <span className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-2"><TrendingUp size={24} className="text-[#8fbf82]" /> {isPod ? "PODCAST ANALYTICS" : "ANALYTICS"}</span>
          </div>
          <p className="text-sm text-white/70 font-semibold mt-1">{isPod ? "How your podcast episodes are performing." : "How your videos & digital kits are performing."}</p>
        </div>
        <div className="flex items-center gap-1.5 bg-black/25 rounded-full p-1" data-testid="analytics-window-tabs">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              data-testid={`window-${w.key}`}
              onClick={() => setWindow(w.key)}
              className={`text-xs font-extrabold px-3 py-1.5 rounded-full transition-colors ${window === w.key ? "bg-[#4f7746] text-white" : "text-white/70 hover:text-white"}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Channel snapshot (all-time) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 mb-5" data-testid="channel-snapshot">
        <Stat icon={Video} label={isPod ? "Episodes" : "Videos"} value={t.videos ?? 0} sub={`${t.shared ?? 0} live`} tint="#7aa7ff" />
        <Stat icon={Eye} label="Total Views" value={(t.views ?? 0).toLocaleString()} tint="#8fbf82" />
        <Stat icon={Heart} label="Loves" value={t.loves ?? 0} tint="#e11d48" />
        <Stat icon={Star} label="Avg Rating" value={t.avg_rating ? t.avg_rating.toFixed(1) : "—"} sub={`${t.ratings ?? 0}`} tint="#eab308" />
        <Stat icon={MessageCircle} label="Comments" value={t.comments ?? 0} tint="#7aa7ff" />
        <Stat icon={MousePointerClick} label="Amazon Clicks" value={t.clicks ?? 0} sub={`${t.ctr ?? 0}% CTR`} tint="#e0a92e" />
        <Stat icon={ShoppingBag} label="Kit Sales" value={t.sales_count ?? 0} tint="#8fbf82" />
        <Stat icon={DollarSign} label="Earnings" value={`$${money.toFixed(2)}`} sub={`$${((t.tips || 0) / 100).toFixed(2)} tips`} tint="#8fbf82" />
      </div>

      {loading && !traffic ? (
        <div className="flex items-center justify-center py-20 text-white/60"><Loader2 className="spin" /></div>
      ) : (
        <>
          {/* Traffic KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-5" data-testid="traffic-kpis">
            <Stat icon={Users} label="Unique Visitors" value={(k.unique_visitors ?? 0).toLocaleString()} tint="#8fbf82" />
            <Stat icon={Eye} label="Page Views" value={(k.pageviews ?? 0).toLocaleString()} tint="#7aa7ff" />
            <Stat icon={Repeat} label="Returning" value={k.returning_visitors ?? 0} tint="#c084fc" />
            <Stat icon={UserPlus} label="New" value={k.new_visitors ?? 0} tint="#8fbf82" />
            <Stat icon={MousePointerClick} label="Clicks" value={k.affiliate_clicks ?? 0} tint="#e0a92e" />
            <Stat icon={Percent} label="Click-Through" value={`${k.ctr ?? 0}%`} tint="#eab308" />
          </div>

          {/* Views trend — auto-fits the selected range (hours for 24h, days for 7/30/All) */}
          <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0e0e13] p-4 mb-4" data-testid="views-trend">
            <h2 className="text-sm font-extrabold text-white mb-3">Views — {rangeLabel}</h2>
            {series.length > 0 ? (
              <Bars data={series} labelLeft={series[0]?.label} labelRight={series[series.length - 1]?.label} valueKey="views" height={110} />
            ) : <p className="text-sm text-white/50">No view data yet.</p>}
          </div>

          {/* Busiest hours */}
          <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0e0e13] p-4 mb-4">
            <h2 className="text-sm font-extrabold text-white mb-3">Busiest hours (UTC)</h2>
            <Bars data={hourly.map((v, i) => ({ label: `${i}:00`, views: v, date: `${i}:00` }))} labelLeft="12a" labelRight="11p" valueKey="views" height={80} />
          </div>

          {/* Referrers + Countries */}
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0e0e13] p-4">
              <h2 className="text-sm font-extrabold text-white mb-2 flex items-center gap-1.5"><Globe size={15} className="text-[#8fbf82]" /> Where they came from</h2>
              {(traffic?.top_referrers || []).length ? (traffic.top_referrers.map((r) => (
                <div key={r.name} className="flex items-center justify-between py-1.5 border-b border-white/5 text-sm">
                  <span className="text-white/80 truncate">{r.name}</span><span className="font-extrabold text-white">{r.count}</span>
                </div>
              ))) : <p className="text-sm text-white/50">No referrers yet.</p>}
            </div>
            <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0e0e13] p-4">
              <h2 className="text-sm font-extrabold text-white mb-2 flex items-center gap-1.5"><Globe size={15} className="text-[#7aa7ff]" /> Countries</h2>
              {(traffic?.top_countries || []).length ? (traffic.top_countries.map((c) => (
                <div key={c.name} className="flex items-center justify-between py-1.5 border-b border-white/5 text-sm">
                  <span className="text-white/80 truncate">{c.name}</span><span className="font-extrabold text-white">{c.count}</span>
                </div>
              ))) : <p className="text-sm text-white/50">No country data yet.</p>}
            </div>
          </div>

          {/* All videos — scrollable window, centered, ~20 rows visible */}
          <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0e0e13] p-4 max-w-3xl mx-auto" data-testid="top-videos">
            <h2 className="text-sm font-extrabold text-white mb-2 flex items-center justify-between">
              <span>{isPod ? "All episodes" : "All videos"}</span>
              <span className="text-[11px] font-semibold text-white/40">{(traffic?.top_videos || []).length} total</span>
            </h2>
            {(traffic?.top_videos || []).length ? (
              <div className="overflow-y-auto rounded-lg" style={{ maxHeight: 880 }} data-testid="top-videos-scroll">
                <table className="w-full text-sm table-fixed">
                  <thead className="sticky top-0 bg-[#0e0e13] z-10">
                    <tr className="text-[10px] uppercase tracking-wide text-white/40 text-left">
                      <th className="py-2 font-bold">Title</th>
                      <th className="py-2 font-bold text-right w-12 sm:w-20">Views</th>
                      <th className="py-2 font-bold text-right w-12 sm:w-20">Clicks</th>
                      <th className="py-2 font-bold text-right pr-1 w-12 sm:w-16">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traffic.top_videos.map((v) => (
                      <tr key={v.id} className="border-t border-white/5 cursor-pointer hover:bg-white/5" onClick={() => navigate(`/community/${v.id}`)} data-testid={`top-video-${v.id}`}>
                        <td className="py-2.5 pr-2 font-semibold text-white truncate">{v.title}</td>
                        <td className="py-2.5 text-right font-extrabold text-white">{v.views}</td>
                        <td className="py-2.5 text-right text-[#e0a92e] font-bold">{v.clicks}</td>
                        <td className="py-2.5 text-right text-white/60 pr-1">{v.ctr}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-white/50">{isPod ? "No episodes yet — publish one to start tracking." : "No videos yet — create one to start tracking."}</p>}
          </div>
        </>
      )}
    </div>
  );
}
